import React from "react";
import { connect } from "react-redux";
import { Col, Form, Row, Table } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";
import { NumericFormat as NumberFormat } from "react-number-format";

import ConditionalDropdown from "../../../layout/conditional-dropdown";
import ConditionalButton from "../../../layout/conditional-button";
import Modal from "../../../layout/modal";

import { get as getAccounts } from "../../../actions/accounts";
import { get as getFinancialAccounts } from "../../../actions/financial.accounts";
import {
  adjustment as createAdjustment,
  candidates as getCandidates,
  finalize as finalizeReconciliation,
  get as getReconciliation,
  save as saveReconciliation,
} from "../../../actions/bank.reconciliations";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";
import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";
import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

const today = () => new Date().toISOString().slice(0, 10);

const financialAccountLabel = (account) => {
  const institution = String(account?.financialInstitution || "").trim();
  const lastFour = String(account?.lastFour || "").trim();
  const suffix = [institution, lastFour ? `*${lastFour}` : ""].filter(Boolean).join(" ");
  return [account?.name, suffix].filter(Boolean).join(" - ") || account?.uuid || "";
};

class BankReconciliationItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {
        uuid: null,
        financialAccountUuid: null,
        statementDate: today(),
        statementStartDate: null,
        statementEndDate: null,
        beginningBalance: 0,
        endingBalance: 0,
        status: "DRAFT",
        notes: "",
      },
      adjustment: {
        show: false,
        type: null,
        title: "",
        accountUuid: null,
        amount: 0,
        entryDate: today(),
        referenceNumber: "",
        memo: "",
      },
      accounts: [],
      financialAccounts: [],
      lines: [],
      selectedLineUuids: [],
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Bank Reconciliation", breadcrumb: "Reconciliation" });
    await this.loadData();
  };

  get uuid() {
    return this.props.params?.uuid;
  }

  get isExisting() {
    return isGuid(this.uuid) && this.uuid !== "add";
  }

  loadData = async () => {
    this.props.loading(true);
    try {
      const [financialAccounts, accounts] = await Promise.all([
        this.props.getFinancialAccounts(null),
        this.props.getAccounts(null),
      ]);
      let data = this.state.data;
      let selectedLineUuids = [];

      if (this.isExisting) {
        const result = await this.props.getReconciliation(this.uuid);
        data = result?.item || data;
        selectedLineUuids = result?.selectedLineUuids || [];
      }

      this.setState({
        data: {
          ...this.state.data,
          ...data,
        },
        accounts: accounts || [],
        financialAccounts: financialAccounts || [],
        selectedLineUuids,
      }, this.loadCandidates);
    } finally {
      this.props.loading(false);
    }
  };

  get financialAccountOptions() {
    return (this.state.financialAccounts || [])
      .filter((account) => account?.isActive !== false)
      .map((account) => ({
        value: account.uuid,
        label: financialAccountLabel(account),
      }));
  }

  get selectedFinancialAccountOption() {
    return this.financialAccountOptions.find((option) => option.value === this.state.data?.financialAccountUuid) || null;
  }

  accountOptions = (type) => (this.state.accounts || [])
    .filter((account) => account?.active !== false)
    .filter((account) => (
      type === "FEE_EXPENSE"
        ? ["EXPENSE", "COGS"].includes(account?.type)
        : account?.type === "REVENUE"
    ))
    .map((account) => {
      const number = String(account?.accountNumber || "").trim();
      const name = String(account?.name || "").trim();
      return {
        value: account.uuid,
        label: [number, name].filter(Boolean).join(" - ") || account.uuid,
      };
    });

  selectedAdjustmentAccountOption = () => {
    const options = this.accountOptions(this.state.adjustment?.type);
    return options.find((option) => option.value === this.state.adjustment?.accountUuid) || null;
  };

  get addItems() {
    return [
      {
        caption: "Fees Expense",
        onClick: () => this.showAdjustmentModal("FEE_EXPENSE"),
        index: 0,
      },
      {
        caption: "Interest Income",
        onClick: () => this.showAdjustmentModal("INTEREST_INCOME"),
        index: 1,
      },
    ];
  }

  onFinancialAccountChange = (option) => {
    this.setState((prev) => ({
      data: {
        ...prev.data,
        financialAccountUuid: option?.value || null,
      },
      selectedLineUuids: [],
    }), this.loadCandidates);
  };

  onChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [name]: value,
      },
    }));
  };

  onMoneyChange = (field) => ({ floatValue }) => {
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [field]: floatValue ?? 0,
      },
    }));
  };

  showAdjustmentModal = (type) => {
    const title = type === "FEE_EXPENSE" ? "Fees Expense" : "Interest Income";
    this.setState({
      adjustment: {
        show: true,
        type,
        title,
        accountUuid: null,
        amount: 0,
        entryDate: this.state.data?.statementDate || today(),
        referenceNumber: "",
        memo: title,
      },
    });
  };

  hideAdjustmentModal = () => {
    this.setState((prev) => ({
      adjustment: {
        ...prev.adjustment,
        show: false,
      },
    }));
  };

  onAdjustmentChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => ({
      adjustment: {
        ...prev.adjustment,
        [name]: value,
      },
    }));
  };

  onAdjustmentAccountChange = (option) => {
    this.setState((prev) => ({
      adjustment: {
        ...prev.adjustment,
        accountUuid: option?.value || null,
      },
    }));
  };

  onAdjustmentAmountChange = ({ floatValue }) => {
    this.setState((prev) => ({
      adjustment: {
        ...prev.adjustment,
        amount: floatValue ?? 0,
      },
    }));
  };

  onAdjustmentSave = async () => {
    const adjustment = this.state.adjustment || {};
    const financialAccountUuid = this.state.data?.financialAccountUuid;
    if (!isGuid(financialAccountUuid)) {
      this.props.sendAlert(["Select a financial account before adding an adjustment."], "error");
      return;
    }

    this.props.loading(true);
    try {
      const result = await this.props.createAdjustment({
        financialAccountUuid,
        adjustmentType: adjustment.type,
        accountUuid: adjustment.accountUuid,
        amount: adjustment.amount,
        entryDate: adjustment.entryDate,
        referenceNumber: adjustment.referenceNumber,
        memo: adjustment.memo,
      });

      if (result?.success === false || !result) {
        this.props.sendAlert([result?.error?.message || "Unable to add adjustment."], "error");
        return;
      }

      this.hideAdjustmentModal();
      await this.loadCandidates();
    } finally {
      this.props.loading(false);
    }
  };

  loadCandidates = async () => {
    const financialAccountUuid = this.state.data?.financialAccountUuid;
    if (!isGuid(financialAccountUuid) && !this.isExisting) return;

    const result = await this.props.getCandidates({
      uuid: this.isExisting ? this.uuid : null,
      financialAccountUuid,
    });

    this.setState((prev) => ({
      lines: result?.items || [],
      selectedLineUuids: result?.selectedLineUuids?.length ? result.selectedLineUuids : prev.selectedLineUuids,
    }));
  };

  isSelected = (lineUuid) => (this.state.selectedLineUuids || []).includes(lineUuid);

  onLineToggle = (lineUuid) => {
    if (this.state.data?.status === "RECONCILED") return;
    this.setState((prev) => {
      const selected = new Set(prev.selectedLineUuids || []);
      if (selected.has(lineUuid)) selected.delete(lineUuid);
      else selected.add(lineUuid);
      return { selectedLineUuids: Array.from(selected) };
    });
  };

  selectedLines = () => {
    const selected = new Set(this.state.selectedLineUuids || []);
    return (this.state.lines || []).filter((line) => selected.has(line.uuid));
  };

  get clearedTotal() {
    return this.selectedLines().reduce((total, line) => {
      return total + Number(line.debitAmount || 0) - Number(line.creditAmount || 0);
    }, 0);
  }

  get difference() {
    const data = this.state.data || {};
    return Number(data.beginningBalance || 0) + this.clearedTotal - Number(data.endingBalance || 0);
  }

  get canFinalize() {
    return this.state.data?.status !== "RECONCILED"
      && isGuid(this.state.data?.uuid)
      && Math.round(this.difference * 100) === 0;
  }

  savePayload = () => ({
    ...this.state.data,
    selectedLineUuids: this.state.selectedLineUuids,
  });

  onSaveClick = async () => {
    this.props.loading(true);
    try {
      const result = await this.props.saveReconciliation(this.savePayload());
      if (result?.success === false || !result) {
        this.props.sendAlert([result?.error?.message || "Unable to save reconciliation."], "error");
        return;
      }

      if (!this.isExisting && isGuid(result.uuid)) {
        window.location.href = `/dashboard/accounting/reconciliations/${result.uuid}`;
        return;
      }

      this.setState((prev) => ({ data: { ...prev.data, ...result } }), this.loadCandidates);
    } finally {
      this.props.loading(false);
    }
  };

  onFinalizeClick = async () => {
    this.props.loading(true);
    try {
      const result = await this.props.finalizeReconciliation(this.state.data?.uuid);
      if (result?.success === false || !result) {
        this.props.sendAlert([result?.error?.message || "Unable to finalize reconciliation."], "error");
        return;
      }
      this.setState((prev) => ({ data: { ...prev.data, ...result } }), this.loadCandidates);
    } finally {
      this.props.loading(false);
    }
  };

  renderLine = (line) => {
    const entry = line?.JournalEntry || {};
    const selected = this.isSelected(line.uuid);
    const disabled = this.state.data?.status === "RECONCILED";

    return (
      <tr key={line.uuid}>
        <td>
          <Form.Check
            checked={selected}
            disabled={disabled}
            onChange={() => this.onLineToggle(line.uuid)}
          />
        </td>
        <td>{formatDate(entry.entryDate)}</td>
        <td>{entry.referenceNumber || ""}</td>
        <td>{line.memo || entry.memo || ""}</td>
        <td>{line.reconciliationType || ""}</td>
        <td className="text-end">{Number(line.debitAmount || 0) ? formatCurrency(Number(line.debitAmount || 0)) : ""}</td>
        <td className="text-end">{Number(line.creditAmount || 0) ? formatCurrency(Number(line.creditAmount || 0)) : ""}</td>
      </tr>
    );
  };

  render() {
    const data = this.state.data || {};
    const readOnly = data.status === "RECONCILED";

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalDropdown
            className="ms-sm-1"
            enabledVariant="outline-primary"
            display={!readOnly}
            enabled={isGuid(data.financialAccountUuid)}
            items={this.addItems}
            icon="plus"
          >
            Add
          </ConditionalDropdown>
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Reconciliation"
            display
            enabled={!readOnly}
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Finalize Reconciliation"
            display
            enabled={this.canFinalize}
            onEnabledClick={this.onFinalizeClick}
            icon="check"
          >
            Finalize
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="g-3">
            <Col xs="12" md="8" xl="5">
              <div className="mb-3 top-label">
                <Select
                  inputId="financialAccountUuid"
                  classNamePrefix="react-select"
                  isClearable
                  isDisabled={readOnly}
                  placeholder="(Select financial account)"
                  options={this.financialAccountOptions}
                  value={this.selectedFinancialAccountOption}
                  onChange={this.onFinancialAccountChange}
                />
                <Form.Label>FINANCIAL ACCOUNT</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control type="date" name="statementDate" value={data.statementDate || ""} disabled={readOnly} onChange={this.onChange} />
                <Form.Label>STATEMENT DATE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control type="date" name="statementStartDate" value={data.statementStartDate || ""} disabled={readOnly} onChange={this.onChange} />
                <Form.Label>START DATE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control type="date" name="statementEndDate" value={data.statementEndDate || ""} disabled={readOnly} onChange={this.onChange} />
                <Form.Label>END DATE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <NumberFormat
                  value={data.beginningBalance ?? 0}
                  thousandSeparator
                  allowNegative
                  disabled={readOnly}
                  inputMode="decimal"
                  decimalScale={2}
                  prefix="$"
                  className="text-end form-control"
                  onValueChange={this.onMoneyChange("beginningBalance")}
                />
                <Form.Label>BEGINNING BALANCE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <NumberFormat
                  value={data.endingBalance ?? 0}
                  thousandSeparator
                  allowNegative
                  disabled={readOnly}
                  inputMode="decimal"
                  decimalScale={2}
                  prefix="$"
                  className="text-end form-control"
                  onValueChange={this.onMoneyChange("endingBalance")}
                />
                <Form.Label>ENDING BALANCE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control className="text-end" value={formatCurrency(this.clearedTotal)} disabled readOnly />
                <Form.Label>CLEARED TOTAL</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control
                  className={`text-end ${Math.round(this.difference * 100) === 0 ? "" : "text-danger"}`}
                  value={formatCurrency(this.difference)}
                  disabled
                  readOnly
                />
                <Form.Label>DIFFERENCE</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" xl="2">
              <div className="mb-3 top-label">
                <Form.Control value={data.status || "DRAFT"} disabled readOnly />
                <Form.Label>STATUS</Form.Label>
              </div>
            </Col>
            <Col xs="12">
              <div className="mb-3 top-label">
                <Form.Control as="textarea" rows={3} name="notes" value={data.notes || ""} disabled={readOnly} onChange={this.onChange} />
                <Form.Label>NOTES</Form.Label>
              </div>
            </Col>
          </Row>
        </Col>

        <Col xs="12">
          <div className="small text-muted mb-2">
            {this.state.selectedLineUuids.length} cleared of {this.state.lines.length} available lines
          </div>
          <Table responsive className="react-table boxed">
            <thead>
              <tr>
                <th className="w-5"></th>
                <th>Date</th>
                <th>Reference</th>
                <th>Memo</th>
                <th>Type</th>
                <th className="text-end">Debit</th>
                <th className="text-end">Credit</th>
              </tr>
            </thead>
            <tbody>
              {(this.state.lines || []).map(this.renderLine)}
            </tbody>
          </Table>
        </Col>

        {this.renderAdjustmentModal()}
      </>
    );
  }

  renderAdjustmentModal = () => {
    const adjustment = this.state.adjustment || {};
    const type = adjustment.type;
    const options = this.accountOptions(type);

    return (
      <Modal
        show={adjustment.show}
        title={adjustment.title || "Adjustment"}
        onCancel={this.hideAdjustmentModal}
        buttons={[
          {
            caption: "Cancel",
            default: true,
            onClick: this.hideAdjustmentModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
          {
            caption: "Save",
            onClick: this.onAdjustmentSave,
            variant: "primary",
            display: true,
            enabled: isGuid(adjustment.accountUuid) && Number(adjustment.amount || 0) > 0,
          },
        ]}
      >
        <Row className="g-3">
          <Col xs="12">
            <div className="mb-3 top-label">
              <Select
                inputId="adjustmentAccountUuid"
                classNamePrefix="react-select"
                isClearable
                placeholder={type === "FEE_EXPENSE" ? "(Select expense account)" : "(Select income account)"}
                options={options}
                value={this.selectedAdjustmentAccountOption()}
                onChange={this.onAdjustmentAccountChange}
              />
              <Form.Label>{type === "FEE_EXPENSE" ? "EXPENSE ACCOUNT" : "INCOME ACCOUNT"}</Form.Label>
            </div>
          </Col>
          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <NumberFormat
                value={adjustment.amount ?? 0}
                thousandSeparator
                allowNegative={false}
                inputMode="decimal"
                decimalScale={2}
                prefix="$"
                className="text-end form-control"
                onValueChange={this.onAdjustmentAmountChange}
              />
              <Form.Label>AMOUNT</Form.Label>
            </div>
          </Col>
          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Form.Control type="date" name="entryDate" value={adjustment.entryDate || ""} onChange={this.onAdjustmentChange} />
              <Form.Label>DATE</Form.Label>
            </div>
          </Col>
          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Form.Control type="text" name="referenceNumber" value={adjustment.referenceNumber || ""} onChange={this.onAdjustmentChange} />
              <Form.Label>REFERENCE</Form.Label>
            </div>
          </Col>
          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Form.Control type="text" name="memo" value={adjustment.memo || ""} onChange={this.onAdjustmentChange} />
              <Form.Label>MEMO</Form.Label>
            </div>
          </Col>
        </Row>
      </Modal>
    );
  };
}

BankReconciliationItem.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  getAccounts: PropTypes.func.isRequired,
  getFinancialAccounts: PropTypes.func.isRequired,
  getReconciliation: PropTypes.func.isRequired,
  getCandidates: PropTypes.func.isRequired,
  saveReconciliation: PropTypes.func.isRequired,
  finalizeReconciliation: PropTypes.func.isRequired,
  createAdjustment: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
};

export default withNavigation(connect(null, {
  loading,
  pageInfo,
  getAccounts,
  getFinancialAccounts,
  getReconciliation,
  getCandidates,
  saveReconciliation,
  finalizeReconciliation,
  createAdjustment,
  sendAlert,
})(BankReconciliationItem));
